import useEditableCell from "@/react/components/table/useEditableCell";
import { cellErrors, KEY } from "@/react/components/table/useTableMutations";
import React, { useCallback, useEffect } from "react";
import Select from "@8x8/oxygen-select";
import { useCustomerNumberSearch } from "@/react/features/smp/dms/useCustomerNumberSearch";

const mapDidsToOptions = (data) =>
  (data || []).map((did) => ({
    value: did.uuid,
    label: did.phoneNumber,
    countryCode: did.country,
  }));

const filterOptions = (inputValue) => (option) =>
  inputValue
    ? option.label.toLowerCase().includes(inputValue.toLowerCase())
    : true;

export function PhoneNumberSelectorCell(phoneCell) {
  const { updateRows, skipAutoResetPageIndex, schema } =
    phoneCell.table.options.meta;
  const { value: phoneValue, onChange: onChangePhone } =
    useEditableCell(phoneCell);

  const didIdCell = phoneCell.row
    .getAllCells()
    .find((c) => c.column.id === schema.fields.didId.spec.key)
    ?.getContext();
  const { value: didIdValue, onChange: onChangeDidId } =
    useEditableCell(didIdCell);

  const countryCodeCell = phoneCell.row
    .getAllCells()
    .find((c) => c.column.id === schema.fields.countryCode.spec.key)
    ?.getContext();
  const { value: countryCodeValue, onChange: onChangeCountryCode } =
    useEditableCell(countryCodeCell);

  const { dids, searchDids } = useCustomerNumberSearch();

  useEffect(() => {
    searchDids();
  }, [searchDids]);

  const errors = cellErrors(phoneCell);

  const options = mapDidsToOptions(dids);

  if (phoneCell.getValue()) {
    options.push({
      value: didIdCell.getValue(),
      label: phoneCell.getValue(),
    });
  }

  const selectValue = options.find((opt) => opt.label === phoneValue);

  // https://react-select.com/async
  const searchAndFilterOptions = useCallback(
    async (phoneNumberSearch) => {
      const filteredOptions = options.filter(filterOptions(phoneNumberSearch));

      if (filteredOptions.length > 0) {
        return filteredOptions;
      }

      const foundDids = await searchDids(phoneNumberSearch);
      return mapDidsToOptions(foundDids).filter(
        filterOptions(phoneNumberSearch)
      );
    },
    [options, searchDids]
  );

  const onDidSelected = (opt) => {
    onChangeDidId(opt?.value);
    onChangePhone(opt?.label);
    onChangeCountryCode(opt?.countryCode);
  };

  const onBlurSelect = () => {
    skipAutoResetPageIndex();
    updateRows(
      [phoneCell.row.original[KEY]],
      new Map([
        [schema.fields.phoneNumber.spec.key, phoneValue],
        [schema.fields.didId.spec.key, didIdValue],
        [schema.fields.countryCode.spec.key, countryCodeValue],
      ]),
      schema
    );
  };

  return (
    <Select
      size={"small"}
      testId={"phone-number-select"}
      isAsync={true}
      loadOptions={searchAndFilterOptions}
      defaultOptions={options}
      value={selectValue}
      onChange={onDidSelected}
      onBlur={onBlurSelect}
      hasError={errors.length > 0}
      isClearable={true}
      isSearchable={true}
      placeholder={`Search customer DIDs`}
    />
  );
}
